/**
 * Fitness Tracker Zustand Store
 *
 * State management for the FitTrack feature.
 * Handles workouts, goals, Google Sheets sync, and UI state.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { fitness, supabase } from '../services/supabaseService';
import { coachRelationships } from '../services/coachService';
import { useCoachStore } from './coachStore';
import {
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  startOfWeek,
  addMonths,
  subMonths,
  addYears,
  subYears,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  format,
  isToday,
  parseISO,
} from 'date-fns';
import { tr } from 'date-fns/locale';
import toast from 'react-hot-toast';

// Exercise type configuration (Turkish)
export const EXERCISE_TYPES = {
  strength: { label: 'Ağırlık', emoji: '🏋️', color: 'blue' },
  cardio: { label: 'Kardiyo', emoji: '🏃', color: 'green' },
  flexibility: { label: 'Esneklik', emoji: '🧘', color: 'purple' },
  sport: { label: 'Spor', emoji: '⚽', color: 'orange' },
  other: { label: 'Diğer', emoji: '🎯', color: 'gray' },
};

// Muscle group configuration (Turkish)
export const MUSCLE_GROUPS = {
  chest: { label: 'Göğüs' },
  back: { label: 'Sırt' },
  shoulders: { label: 'Omuz' },
  legs: { label: 'Bacak' },
  biceps: { label: 'Biceps' },
  triceps: { label: 'Triceps' },
  core: { label: 'Karın' },
  full_body: { label: 'Tüm Vücut' },
};

export const useFitnessStore = create(
  subscribeWithSelector((set, get) => ({
    // =====================================================
    // STATE
    // =====================================================

    // Workouts
    workouts: [],
    isLoadingWorkouts: false,

    // Goals
    goals: [],
    isLoadingGoals: false,

    // Personal Records
    personalRecords: [],
    isLoadingPRs: false,

    // Summary stats
    summaryStats: null,
    isLoadingSummary: false,

    // Google Connection
    isGoogleConnected: false,
    googleEmail: null,
    isConnecting: false,

    // Spreadsheets
    availableSpreadsheets: [],
    connectedSpreadsheets: [],
    selectedSpreadsheet: null,
    isLoadingSpreadsheets: false,

    // Schema mapping
    schemaMapping: null,
    isAnalyzingSchema: false,

    // Sync status
    isSyncing: false,
    lastSyncedAt: null,

    // Analytics
    exerciseProgress: {},
    workoutFrequency: {},
    muscleGroupVolume: {},
    isLoadingAnalytics: false,

    // Exercise names for autocomplete
    exerciseNames: [],

    // Favorite exercises
    favoriteExercises: [],
    isLoadingFavorites: false,
    _favoritesLoaded: false,

    // Scheduled workouts
    scheduledWorkouts: [],
    isLoadingScheduledWorkouts: false,
    _scheduledWorkoutsSubscription: null, // Realtime subscription

    // Navigation
    currentMonth: new Date(),
    currentWeekStart: startOfWeek(new Date(), { weekStartsOn: 1 }),
    selectedDay: format(new Date(), 'yyyy-MM-dd'),
    viewMode: 'calendar', // 'calendar' | 'list' | 'analytics'
    calendarViewMode: 'daily', // 'weekly' | 'daily' | 'monthly'
    showAllClientsCalendar: false, // When in "Kendim" mode, show all clients' workouts too

    // Request tracking to prevent race conditions
    _loadRequestId: 0,
    _loadScheduledRequestId: 0,
    _loadSummaryRequestId: 0,
    _isInitialized: false,
    _isInitializing: false,
    _lastInitClientId: null, // Track which client we initialized for
    _lastInitTime: 0, // Debounce protection

    // Modals
    isAddWorkoutModalOpen: false,
    isGoalModalOpen: false,
    isConnectSheetsModalOpen: false,
    isSchemaMapperOpen: false,
    isDayDetailModalOpen: false,
    isScheduleWorkoutModalOpen: false,
    selectedWorkout: null,
    selectedDate: null,
    selectedDayWorkouts: [],
    selectedDayClientId: null, // Track which client's workouts are shown (for coach mode)
    selectedScheduledWorkout: null,
    scheduleModalDate: null,
    scheduleModalTime: null,
    // For linking scheduled workout to fitness workout
    completingScheduledWorkout: null,

    // Exercise search
    exerciseSearchResults: [],
    exerciseSearchQuery: '',
    isExerciseSearchModalOpen: false,
    isSearchingExercises: false,

    // Live Workout (real-time session)
    isLiveWorkoutModalOpen: false,
    liveWorkoutData: null, // The scheduled workout being executed
    isLiveWorkoutSaving: false,

    // =====================================================
    // GOOGLE OAUTH ACTIONS
    // =====================================================

    checkGoogleConnection: async () => {
      try {
        const result = await fitness.isGoogleConnected();
        set({
          isGoogleConnected: result.connected,
          googleEmail: result.email || null,
        });
      } catch (error) {
        console.error('Error checking Google connection:', error);
      }
    },

    initiateGoogleAuth: async () => {
      set({ isConnecting: true });
      try {
        const { authUrl, state } = await fitness.getGoogleAuthUrl();

        // Store state for verification
        sessionStorage.setItem('google_oauth_state', state);

        // Redirect to Google OAuth
        window.location.href = authUrl;
      } catch (error) {
        console.error('Error initiating Google auth:', error);
        toast.error('Google bağlantısı başlatılamadı');
        set({ isConnecting: false });
      }
    },

    handleOAuthCallback: async (code, state) => {
      set({ isConnecting: true });
      try {
        // Verify state
        const storedState = sessionStorage.getItem('google_oauth_state');
        if (state !== storedState) {
          throw new Error('State mismatch - possible CSRF attack');
        }

        const result = await fitness.exchangeGoogleCode(code);

        sessionStorage.removeItem('google_oauth_state');

        set({
          isGoogleConnected: true,
          googleEmail: result.email,
          isConnecting: false,
        });

        toast.success('Google hesabınız bağlandı!');

        // Load spreadsheets
        get().loadAvailableSpreadsheets();

        return true;
      } catch (error) {
        console.error('OAuth callback error:', error);
        toast.error('Google bağlantısı başarısız');
        set({ isConnecting: false });
        return false;
      }
    },

    disconnectGoogle: async () => {
      try {
        await fitness.disconnectGoogle();
        set({
          isGoogleConnected: false,
          googleEmail: null,
          availableSpreadsheets: [],
          connectedSpreadsheets: [],
        });
        toast.success('Google hesabı bağlantısı kaldırıldı');
      } catch (error) {
        console.error('Error disconnecting Google:', error);
        toast.error('Bağlantı kaldırılamadı');
      }
    },

    // =====================================================
    // SPREADSHEET ACTIONS
    // =====================================================

    loadAvailableSpreadsheets: async () => {
      set({ isLoadingSpreadsheets: true });
      try {
        const spreadsheets = await fitness.listGoogleSpreadsheets();
        set({ availableSpreadsheets: spreadsheets, isLoadingSpreadsheets: false });
      } catch (error) {
        console.error('Error loading spreadsheets:', error);
        set({ isLoadingSpreadsheets: false });
        if (error.message?.includes('not connected')) {
          set({ isGoogleConnected: false });
        }
      }
    },

    loadConnectedSpreadsheets: async () => {
      try {
        const connected = await fitness.getConnectedSpreadsheets();
        set({ connectedSpreadsheets: connected });
      } catch (error) {
        console.error('Error loading connected spreadsheets:', error);
      }
    },

    selectSpreadsheet: async (spreadsheet) => {
      set({ selectedSpreadsheet: spreadsheet, isAnalyzingSchema: true });

      try {
        // Read first 20 rows to analyze structure
        const data = await fitness.readSheetData(spreadsheet.id, 'A1:Z20');

        // Check if this spreadsheet has a saved mapping
        const { connectedSpreadsheets } = get();
        const existingConnection = connectedSpreadsheets.find(
          s => s.spreadsheet_id === spreadsheet.id
        );
        const savedMapping = existingConnection?.schema_mapping || null;

        set({
          schemaMapping: {
            values: data.values || [],
            spreadsheetId: spreadsheet.id,
            spreadsheetName: spreadsheet.name,
            savedMapping, // Pass saved mapping if exists
          },
          isAnalyzingSchema: false,
          isSchemaMapperOpen: true,
        });
      } catch (error) {
        console.error('Error analyzing spreadsheet:', error);
        toast.error('Tablo yapısı analiz edilemedi');
        set({ isAnalyzingSchema: false });
      }
    },

    confirmSchemaMapping: async (mapping) => {
      const { selectedSpreadsheet } = get();
      if (!selectedSpreadsheet) return;

      try {
        await fitness.connectSpreadsheet(
          selectedSpreadsheet.id,
          selectedSpreadsheet.name,
          mapping
        );

        set({ isSchemaMapperOpen: false, isConnectSheetsModalOpen: false });
        toast.success('Tablo bağlandı!');

        // Reload connected spreadsheets
        get().loadConnectedSpreadsheets();
      } catch (error) {
        console.error('Error connecting spreadsheet:', error);
        toast.error('Tablo bağlanamadı');
      }
    },

    // =====================================================
    // WORKOUT ACTIONS
    // =====================================================

    loadWorkouts: async (loadAll = false, force = false) => {
      const { currentMonth, currentWeekStart, selectedDay, calendarViewMode, _loadRequestId, isLoadingWorkouts } = get();
      const { viewingClientId } = useCoachStore.getState();

      // Skip if already loading (prevents duplicate requests) - unless forced
      if (isLoadingWorkouts && !force) return;

      // Increment request ID to track this specific request
      const thisRequestId = _loadRequestId + 1;
      set({ _loadRequestId: thisRequestId, isLoadingWorkouts: true });

      let start, end;
      if (loadAll) {
        // Load all workouts (for analytics)
        start = '2020-01-01';
        end = '2030-12-31';
      } else if (calendarViewMode === 'yearly') {
        // Load whole year for yearly view
        const year = currentMonth.getFullYear();
        start = `${year}-01-01`;
        end = `${year}-12-31`;
      } else if (calendarViewMode === 'quarterly') {
        // Load 3 months for quarterly view
        start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
        end = format(endOfMonth(addMonths(currentMonth, 2)), 'yyyy-MM-dd');
      } else if (calendarViewMode === 'weekly') {
        // Load week for weekly view
        start = format(currentWeekStart, 'yyyy-MM-dd');
        end = format(addDays(currentWeekStart, 6), 'yyyy-MM-dd');
      } else if (calendarViewMode === 'daily') {
        // Load single day for daily view
        start = selectedDay;
        end = selectedDay;
      } else {
        // Load only current month (for monthly view)
        start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
        end = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      }

      try {
        let workouts;
        if (viewingClientId) {
          // Coach mode: load client's workouts
          workouts = await coachRelationships.getClientWorkouts(viewingClientId, start, end);
        } else {
          // Normal mode: load own workouts
          workouts = await fitness.getWorkouts(start, end);
        }

        // Only update state if this is still the latest request
        if (get()._loadRequestId === thisRequestId) {
          set({ workouts: workouts || [], isLoadingWorkouts: false });
        }
      } catch (error) {
        console.error('Error loading workouts:', error);
        // Only update state if this is still the latest request
        if (get()._loadRequestId === thisRequestId) {
          set({ isLoadingWorkouts: false });
        }
      }
    },

    addWorkout: async (workoutData) => {
      const { viewingClientId, viewingClientInfo } = useCoachStore.getState();
      const { completingScheduledWorkout } = get();

      // If completing a scheduled workout, add the link
      const dataWithScheduleLink = completingScheduledWorkout
        ? { ...workoutData, scheduled_workout_id: completingScheduledWorkout.id }
        : workoutData;

      try {
        let workout;
        if (viewingClientId) {
          // Coach mode: create workout for client
          workout = await coachRelationships.createClientWorkout(viewingClientId, dataWithScheduleLink);
          toast.success(`${viewingClientInfo?.display_name || 'Öğrenci'} için antrenman eklendi!`);
        } else {
          // Normal mode: create own workout
          workout = await fitness.createWorkout(dataWithScheduleLink);

          // Check for PRs (only for own workouts)
          try {
            const newPRs = await fitness.checkAndUpdatePRs(workout);
            if (newPRs && newPRs.length > 0) {
              toast.success(`Yeni PR! ${newPRs.map(pr => pr.exercise_name).join(', ')}`, {
                icon: '🏆',
                duration: 5000,
              });
            }
          } catch (prError) {
            console.error('Error checking PRs:', prError);
            // Don't fail the whole operation
          }

          // Also write to connected spreadsheet if configured (only for own workouts)
          const { connectedSpreadsheets } = get();
          const activeSheet = connectedSpreadsheets.find(s => s.is_active);
          if (activeSheet) {
            try {
              const mapping = activeSheet.schema_mapping;
              const row = mapWorkoutToSheetRow(workout, mapping);
              await fitness.appendWorkoutToSheet(
                activeSheet.spreadsheet_id,
                mapping.sheet_name || 'Sheet1',
                row
              );
            } catch (sheetError) {
              console.error('Error writing to sheet:', sheetError);
              // Don't fail the whole operation
            }
          }

          toast.success('Antrenman eklendi!');
        }

        // If completing a scheduled workout, mark it as completed
        if (completingScheduledWorkout) {
          try {
            await get().updateScheduledWorkout(completingScheduledWorkout.id, { status: 'completed' });
          } catch (e) {
            console.error('Error marking scheduled workout as completed:', e);
          }
        }

        set({ isAddWorkoutModalOpen: false, selectedWorkout: null, completingScheduledWorkout: null });
        // Batch reload - only essential data, in parallel (force to bypass loading guard)
        await get()._loadCalendarData(true);
      } catch (error) {
        console.error('Error adding workout:', error);
        toast.error('Antrenman eklenemedi');
      }
    },

    updateWorkout: async (workoutId, updates) => {
      const { viewingClientId } = useCoachStore.getState();
      const { selectedDayClientId } = get();

      // Use selectedDayClientId if viewing a client's workouts from day detail modal
      const targetClientId = viewingClientId || selectedDayClientId;

      console.log('[fitnessStore.updateWorkout] Starting update:', {
        workoutId,
        updates,
        viewingClientId,
        selectedDayClientId,
        targetClientId,
        isCoachMode: !!targetClientId
      });

      try {
        let result;
        if (targetClientId) {
          // Coach mode: update client's workout
          console.log('[fitnessStore.updateWorkout] Coach mode - updating client workout');
          result = await coachRelationships.updateClientWorkout(targetClientId, workoutId, updates);
        } else {
          // Normal mode: update own workout
          console.log('[fitnessStore.updateWorkout] Normal mode - updating own workout');
          result = await fitness.updateWorkout(workoutId, updates);
        }
        console.log('[fitnessStore.updateWorkout] Update successful:', result);
        set({ selectedWorkout: null, isAddWorkoutModalOpen: false });
        toast.success('Antrenman güncellendi');
        get().loadWorkouts(false, true);
      } catch (error) {
        console.error('[fitnessStore.updateWorkout] Error:', error);
        toast.error(`Antrenman güncellenemedi: ${error.message || 'Bilinmeyen hata'}`);
      }
    },

    deleteWorkout: async (workoutId) => {
      const { viewingClientId } = useCoachStore.getState();
      const { selectedDayClientId } = get();

      // Use selectedDayClientId if viewing a client's workouts from day detail modal
      const targetClientId = viewingClientId || selectedDayClientId;

      try {
        if (targetClientId) {
          // Coach mode: delete client's workout
          await coachRelationships.deleteClientWorkout(targetClientId, workoutId);
        } else {
          // Normal mode: delete own workout
          await fitness.deleteWorkout(workoutId);
        }
        set({ selectedWorkout: null, isAddWorkoutModalOpen: false });
        toast.success('Antrenman silindi');
        // Only reload workouts (force to bypass loading guard)
        get().loadWorkouts(false, true);
      } catch (error) {
        console.error('Error deleting workout:', error);
        toast.error('Antrenman silinemedi');
      }
    },

    loadExerciseNames: async () => {
      try {
        const names = await fitness.getExerciseNames();
        set({ exerciseNames: names });
      } catch (error) {
        console.error('Error loading exercise names:', error);
      }
    },

    // =====================================================
    // FAVORITE EXERCISES
    // =====================================================

    loadFavoriteExercises: async (force = false) => {
      const { isLoadingFavorites, _favoritesLoaded } = get();

      // Skip if already loaded (unless forced)
      if (!force && _favoritesLoaded) return;

      // Skip if already loading
      if (isLoadingFavorites) return;

      set({ isLoadingFavorites: true });
      try {
        const favorites = await fitness.getFavoriteExercises();
        set({ favoriteExercises: favorites, isLoadingFavorites: false, _favoritesLoaded: true });
      } catch (error) {
        console.error('Error loading favorite exercises:', error);
        set({ isLoadingFavorites: false });
      }
    },

    addFavoriteExercise: async (exerciseName) => {
      try {
        await fitness.addFavoriteExercise(exerciseName);
        // Optimistic update
        set(state => ({
          favoriteExercises: [exerciseName, ...state.favoriteExercises.filter(e => e !== exerciseName)]
        }));
        toast.success('Favorilere eklendi');
      } catch (error) {
        console.error('Error adding favorite:', error);
        toast.error('Favorilere eklenemedi');
      }
    },

    removeFavoriteExercise: async (exerciseName) => {
      try {
        await fitness.removeFavoriteExercise(exerciseName);
        // Optimistic update
        set(state => ({
          favoriteExercises: state.favoriteExercises.filter(e => e !== exerciseName)
        }));
        toast.success('Favorilerden kaldırıldı');
      } catch (error) {
        console.error('Error removing favorite:', error);
        toast.error('Favorilerden kaldırılamadı');
      }
    },

    toggleFavoriteExercise: async (exerciseName) => {
      const { favoriteExercises } = get();
      if (favoriteExercises.includes(exerciseName)) {
        await get().removeFavoriteExercise(exerciseName);
      } else {
        await get().addFavoriteExercise(exerciseName);
      }
    },

    isFavoriteExercise: (exerciseName) => {
      return get().favoriteExercises.includes(exerciseName);
    },

    // =====================================================
    // SCHEDULED WORKOUT ACTIONS
    // =====================================================

    loadScheduledWorkouts: async (force = false) => {
      const { calendarViewMode, currentWeekStart, selectedDay, currentMonth, _loadScheduledRequestId, isLoadingScheduledWorkouts, showAllClientsCalendar } = get();
      const { viewingClientId } = useCoachStore.getState();

      // Skip if already loading (prevents duplicate requests) - unless forced
      if (isLoadingScheduledWorkouts && !force) return;

      // Increment request ID to track this specific request
      const thisRequestId = _loadScheduledRequestId + 1;
      set({ _loadScheduledRequestId: thisRequestId, isLoadingScheduledWorkouts: true });

      let start, end;
      if (calendarViewMode === 'weekly') {
        start = format(currentWeekStart, 'yyyy-MM-dd');
        end = format(addDays(currentWeekStart, 6), 'yyyy-MM-dd');
      } else if (calendarViewMode === 'daily') {
        start = selectedDay;
        end = selectedDay;
      } else if (calendarViewMode === 'quarterly') {
        // For quarterly view, load 3 months starting from currentMonth
        start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
        end = format(endOfMonth(addMonths(currentMonth, 2)), 'yyyy-MM-dd');
      } else if (calendarViewMode === 'yearly') {
        // For yearly view, load all 12 months of the year
        const yearStart = startOfYear(currentMonth);
        start = format(yearStart, 'yyyy-MM-dd');
        end = format(endOfYear(currentMonth), 'yyyy-MM-dd');
      } else {
        // For monthly view, load the whole month
        start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
        end = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      }

      try {
        let scheduled = [];

        if (viewingClientId) {
          // Coach viewing specific client's scheduled workouts
          scheduled = await coachRelationships.getClientScheduledWorkouts(viewingClientId, start, end);
        } else {
          // Viewing own calendar ("Kendim")
          // Get current user ID for filtering
          const { data: { user } } = await supabase.auth.getUser();
          const currentUserId = user?.id;

          if (showAllClientsCalendar) {
            // Load all: own workouts + all client workouts
            let ownWorkouts = [];
            try {
              ownWorkouts = await fitness.getScheduledWorkouts(start, end);
              // Filter to only own workouts (user_id matches)
              ownWorkouts = (ownWorkouts || []).filter(w => w.user_id === currentUserId);
            } catch (e) {
              console.error('Error loading own scheduled workouts:', e);
            }

            let clientWorkouts = [];
            try {
              clientWorkouts = await coachRelationships.getAllClientsScheduledWorkouts(start, end);
            } catch (e) {
              // Not a coach or function doesn't exist - that's OK
            }

            // Combine: own workouts + client workouts
            const workoutMap = new Map();
            (ownWorkouts || []).forEach(w => workoutMap.set(w.id, w));
            // Client workouts override (they have client_name for display)
            (clientWorkouts || []).forEach(w => workoutMap.set(w.id, w));
            scheduled = Array.from(workoutMap.values());
          } else {
            // Only load own workouts (filter out where coach_id is set but user_id is different)
            let ownWorkouts = [];
            try {
              ownWorkouts = await fitness.getScheduledWorkouts(start, end);
              // Filter to only workouts where current user is the owner
              ownWorkouts = (ownWorkouts || []).filter(w => w.user_id === currentUserId);
            } catch (e) {
              console.error('Error loading own scheduled workouts:', e);
            }
            scheduled = ownWorkouts;
          }
        }

        // Only update state if this is still the latest request
        if (get()._loadScheduledRequestId === thisRequestId) {
          set({ scheduledWorkouts: scheduled, isLoadingScheduledWorkouts: false });
        }
      } catch (error) {
        console.error('Error loading scheduled workouts:', error);
        // Only update state if this is still the latest request
        if (get()._loadScheduledRequestId === thisRequestId) {
          set({ isLoadingScheduledWorkouts: false });
        }
      }
    },

    // Subscribe to realtime updates for scheduled workouts
    subscribeToScheduledWorkouts: async () => {
      const { _scheduledWorkoutsSubscription } = get();

      // Don't subscribe if already subscribed
      if (_scheduledWorkoutsSubscription) {
        console.log('[Realtime] Already subscribed, skipping');
        return;
      }

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        console.log('[Realtime] Subscribing with user ID:', user.id);
        console.log('[Realtime] Creating TWO separate channels:');
        console.log('[Realtime]   - User channel: user_id=eq.' + user.id);
        console.log('[Realtime]   - Coach channel: coach_id=eq.' + user.id);

        // Realtime event handler - shared between user and coach subscriptions
        const handleInsert = (payload) => {
          const { scheduledWorkouts } = get();
          if (payload.new && !scheduledWorkouts.find(w => w.id === payload.new.id)) {
            set({ scheduledWorkouts: [...scheduledWorkouts, payload.new] });
          }
        };

        const handleUpdate = (payload) => {
          console.log('[Realtime UPDATE] Received:', {
            workoutId: payload.new?.id,
            planned_exercises: payload.new?.planned_exercises?.length,
            status: payload.new?.status,
            updated_at: payload.new?.updated_at,
          });

          const { selectedScheduledWorkout, liveWorkoutData, scheduledWorkouts } = get();

          // Update the workout in scheduledWorkouts list - preserve joined data (avatars, names)
          const updatedList = scheduledWorkouts.map(w =>
            w.id === payload.new?.id ? { ...w, ...payload.new } : w
          );

          // Batch all updates into a single set() call to avoid React render warnings
          const updates = { scheduledWorkouts: updatedList };

          // If the updated workout is currently open in schedule modal, preserve joined data
          if (selectedScheduledWorkout?.id === payload.new?.id) {
            console.log('[Realtime UPDATE] Updating selectedScheduledWorkout, new status:', payload.new?.status);
            updates.selectedScheduledWorkout = { ...selectedScheduledWorkout, ...payload.new };
          }

          // If the updated workout is currently open in live workout modal, preserve joined data (avatars)
          if (liveWorkoutData?.id === payload.new?.id) {
            console.log('[Realtime UPDATE] Updating liveWorkoutData with:', {
              exercises: payload.new?.planned_exercises,
            });
            updates.liveWorkoutData = { ...liveWorkoutData, ...payload.new };
          }

          // Single batched state update
          set(updates);
        };

        const handleDelete = (payload) => {
          const { scheduledWorkouts, selectedScheduledWorkout, liveWorkoutData } = get();
          set({
            scheduledWorkouts: scheduledWorkouts.filter(w => w.id !== payload.old?.id),
            ...(selectedScheduledWorkout?.id === payload.old?.id && {
              selectedScheduledWorkout: null,
              isScheduleWorkoutModalOpen: false,
            }),
            ...(liveWorkoutData?.id === payload.old?.id && {
              liveWorkoutData: null,
              isLiveWorkoutModalOpen: false,
            }),
          });
        };

        // Channel 1: Subscribe to OWN workouts (user_id = current user)
        const userChannel = supabase
          .channel('scheduled_workouts_user_' + user.id)
          .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'scheduled_workouts', filter: `user_id=eq.${user.id}` }, handleInsert)
          .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'scheduled_workouts', filter: `user_id=eq.${user.id}` }, handleUpdate)
          .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'scheduled_workouts', filter: `user_id=eq.${user.id}` }, handleDelete)
          .subscribe((status) => {
            console.log('[Realtime] User channel status:', status);
          });

        // Channel 2: Subscribe to CLIENT workouts (coach_id = current user) - for coaches
        const coachChannel = supabase
          .channel('scheduled_workouts_coach_' + user.id)
          .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'scheduled_workouts', filter: `coach_id=eq.${user.id}` }, handleInsert)
          .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'scheduled_workouts', filter: `coach_id=eq.${user.id}` }, handleUpdate)
          .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'scheduled_workouts', filter: `coach_id=eq.${user.id}` }, handleDelete)
          .subscribe((status) => {
            console.log('[Realtime] Coach channel status:', status);
          });

        set({ _scheduledWorkoutsSubscription: { userChannel, coachChannel } });
      } catch (error) {
        console.error('Error subscribing to scheduled workouts:', error);
      }
    },

    // Unsubscribe from realtime updates
    unsubscribeFromScheduledWorkouts: () => {
      const { _scheduledWorkoutsSubscription } = get();
      if (_scheduledWorkoutsSubscription) {
        // Handle both old single-channel and new dual-channel structure
        if (_scheduledWorkoutsSubscription.userChannel) {
          supabase.removeChannel(_scheduledWorkoutsSubscription.userChannel);
        }
        if (_scheduledWorkoutsSubscription.coachChannel) {
          supabase.removeChannel(_scheduledWorkoutsSubscription.coachChannel);
        }
        // Fallback for old structure
        if (!_scheduledWorkoutsSubscription.userChannel && !_scheduledWorkoutsSubscription.coachChannel) {
          supabase.removeChannel(_scheduledWorkoutsSubscription);
        }
        set({ _scheduledWorkoutsSubscription: null });
      }
    },

    addScheduledWorkout: async (workoutData) => {
      const { viewingClientId } = useCoachStore.getState();
      const { data: { user } } = await supabase.auth.getUser();
      const currentUserId = user?.id;

      // Determine target client: either from viewingClientId or from workoutData.user_id
      const targetClientId = viewingClientId || (workoutData.user_id && workoutData.user_id !== currentUserId ? workoutData.user_id : null);

      try {
        if (targetClientId) {
          // Creating schedule for a client (either via viewingClientId or user_id in data)
          // This handles both explicit coach mode and when workout data contains client's user_id
          await coachRelationships.createClientScheduledWorkout(targetClientId, workoutData);
        } else {
          // User creating own schedule
          await fitness.createScheduledWorkout(workoutData);
        }
        set({ isScheduleWorkoutModalOpen: false, selectedScheduledWorkout: null });
        toast.success('Antrenman planlandı!');
        get().loadScheduledWorkouts(true);
      } catch (error) {
        console.error('Error scheduling workout:', error);
        toast.error('Antrenman planlanamadı');
      }
    },

    updateScheduledWorkout: async (workoutId, updates, options = {}) => {
      const { closeModal = false } = options;
      const { viewingClientId } = useCoachStore.getState();
      const { selectedScheduledWorkout } = get();

      try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        const currentUserId = user?.id;

        // Determine if this workout belongs to a client
        const workoutOwnerId = selectedScheduledWorkout?.user_id || updates.user_id;
        const isClientWorkout = workoutOwnerId && workoutOwnerId !== currentUserId;

        if (viewingClientId || isClientWorkout) {
          // Updating client's schedule (either via viewingClientId or workout belongs to different user)
          const clientId = viewingClientId || workoutOwnerId;
          await coachRelationships.updateClientScheduledWorkout(clientId, workoutId, updates);
        } else {
          // User updating own schedule
          await fitness.updateScheduledWorkout(workoutId, updates);
        }
        if (closeModal) {
          set({ selectedScheduledWorkout: null, isScheduleWorkoutModalOpen: false });
        }
        toast.success('Plan güncellendi');
        get().loadScheduledWorkouts(true);
      } catch (error) {
        console.error('Error updating scheduled workout:', error);
        toast.error('Plan güncellenemedi');
      }
    },

    deleteScheduledWorkout: async (workoutId) => {
      const { viewingClientId, isActingAsCoach } = useCoachStore.getState();
      const { selectedScheduledWorkout } = get();

      try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        const currentUserId = user?.id;

        // Determine if this workout belongs to a client
        const workoutOwnerId = selectedScheduledWorkout?.user_id;
        const isClientWorkout = workoutOwnerId && workoutOwnerId !== currentUserId;

        if (viewingClientId || (isActingAsCoach && isClientWorkout)) {
          // Coach deleting client's schedule
          const clientId = viewingClientId || workoutOwnerId;
          await coachRelationships.deleteClientScheduledWorkout(clientId, workoutId);
        } else {
          // User deleting own schedule
          await fitness.deleteScheduledWorkout(workoutId);
        }
        set({ selectedScheduledWorkout: null, isScheduleWorkoutModalOpen: false });
        toast.success('Plan silindi');
        get().loadScheduledWorkouts(true);
      } catch (error) {
        console.error('Error deleting scheduled workout:', error);
        toast.error('Plan silinemedi');
      }
    },

    markScheduledWorkoutMissed: async (scheduledId) => {
      const { viewingClientId } = useCoachStore.getState();
      try {
        if (viewingClientId) {
          // Coach marking client's schedule as missed
          await coachRelationships.updateClientScheduledWorkout(viewingClientId, scheduledId, { status: 'missed' });
        } else {
          // User marking own schedule as missed
          await fitness.markScheduledWorkoutMissed(scheduledId);
        }
        toast.success('Plan kaçırıldı olarak işaretlendi');
        get().loadScheduledWorkouts(true);
      } catch (error) {
        console.error('Error marking workout as missed:', error);
        toast.error('İşaretlenemedi');
      }
    },

    // =====================================================
    // GOALS ACTIONS
    // =====================================================

    loadGoals: async (force = false) => {
      const { isLoadingGoals } = get();
      // Skip if already loading (unless forced)
      if (isLoadingGoals && !force) return;

      set({ isLoadingGoals: true });
      try {
        const goals = await fitness.getGoals('active');
        set({ goals, isLoadingGoals: false });
      } catch (error) {
        console.error('Error loading goals:', error);
        set({ isLoadingGoals: false });
      }
    },

    addGoal: async (goalData) => {
      try {
        await fitness.createGoal(goalData);
        set({ isGoalModalOpen: false });
        toast.success('Hedef eklendi!');
        get().loadGoals(true);
      } catch (error) {
        console.error('Error adding goal:', error);
        toast.error('Hedef eklenemedi');
      }
    },

    updateGoal: async (goalId, updates) => {
      try {
        await fitness.updateGoal(goalId, updates);
        get().loadGoals(true);
      } catch (error) {
        console.error('Error updating goal:', error);
        toast.error('Hedef güncellenemedi');
      }
    },

    completeGoal: async (goalId) => {
      try {
        await fitness.completeGoal(goalId);
        toast.success('Hedef tamamlandı! 🎉');
        get().loadGoals(true);
      } catch (error) {
        console.error('Error completing goal:', error);
      }
    },

    deleteGoal: async (goalId) => {
      try {
        await fitness.deleteGoal(goalId);
        toast.success('Hedef silindi');
        get().loadGoals(true);
      } catch (error) {
        console.error('Error deleting goal:', error);
        toast.error('Hedef silinemedi');
      }
    },

    // =====================================================
    // PRs & ANALYTICS
    // =====================================================

    loadPRs: async (force = false) => {
      const { viewingClientId } = useCoachStore.getState();
      const { isLoadingPRs } = get();

      // Skip if already loading (unless forced)
      if (isLoadingPRs && !force) return;

      set({ isLoadingPRs: true });
      try {
        let prs;
        if (viewingClientId) {
          // Coach mode: load client's PRs
          prs = await coachRelationships.getClientPRs(viewingClientId);
        } else {
          // Normal mode: load own PRs
          prs = await fitness.getPRs();
        }
        set({ personalRecords: prs, isLoadingPRs: false });
      } catch (error) {
        console.error('Error loading PRs:', error);
        set({ isLoadingPRs: false });
      }
    },

    loadSummaryStats: async (force = false) => {
      const { viewingClientId } = useCoachStore.getState();
      const { _loadSummaryRequestId, isLoadingSummary } = get();

      // Skip if already loading (prevents duplicate requests) - unless forced
      if (isLoadingSummary && !force) return;

      // Increment request ID to track this specific request
      const thisRequestId = _loadSummaryRequestId + 1;
      set({ _loadSummaryRequestId: thisRequestId, isLoadingSummary: true });

      try {
        let stats;
        if (viewingClientId) {
          // Coach mode: load client's summary stats
          stats = await coachRelationships.getClientSummaryStats(viewingClientId);
        } else {
          // Normal mode: load own summary stats
          stats = await fitness.getSummaryStats();
        }
        // Only update state if this is still the latest request
        if (get()._loadSummaryRequestId === thisRequestId) {
          set({ summaryStats: stats, isLoadingSummary: false });
        }
      } catch (error) {
        console.error('Error loading summary stats:', error);
        // Only update state if this is still the latest request
        if (get()._loadSummaryRequestId === thisRequestId) {
          set({ isLoadingSummary: false });
        }
      }
    },

    loadAnalytics: async (force = false) => {
      const { viewingClientId } = useCoachStore.getState();
      const { isLoadingAnalytics } = get();

      // Skip if already loading (unless forced)
      if (isLoadingAnalytics && !force) return;

      set({ isLoadingAnalytics: true });
      try {
        let frequency, muscleVolume;
        if (viewingClientId) {
          // Coach mode: load client's analytics
          [frequency, muscleVolume] = await Promise.all([
            coachRelationships.getClientWorkoutFrequency(viewingClientId, 30),
            coachRelationships.getClientMuscleVolume(viewingClientId, 7),
          ]);
        } else {
          // Normal mode: load own analytics
          [frequency, muscleVolume] = await Promise.all([
            fitness.getWorkoutFrequency(30),
            fitness.getMuscleGroupVolume(7),
          ]);
        }

        set({
          workoutFrequency: frequency,
          muscleGroupVolume: muscleVolume,
          isLoadingAnalytics: false,
        });
      } catch (error) {
        console.error('Error loading analytics:', error);
        set({ isLoadingAnalytics: false });
      }
    },

    loadExerciseProgress: async (exerciseName) => {
      const { viewingClientId } = useCoachStore.getState();
      try {
        let progress;
        if (viewingClientId) {
          // Coach mode: load client's exercise progress
          progress = await coachRelationships.getClientExerciseProgress(viewingClientId, exerciseName, 90);
        } else {
          // Normal mode: load own exercise progress
          progress = await fitness.getExerciseProgress(exerciseName, 90);
        }
        set(state => ({
          exerciseProgress: { ...state.exerciseProgress, [exerciseName]: progress },
        }));
      } catch (error) {
        console.error('Error loading exercise progress:', error);
      }
    },

    // =====================================================
    // NAVIGATION
    // =====================================================

    // Batched calendar data loader - prevents duplicate parallel requests
    _loadCalendarData: async (force = false) => {
      const { isLoadingWorkouts, isLoadingScheduledWorkouts } = get();
      // Don't start new loads if already loading (unless forced)
      if (!force && (isLoadingWorkouts || isLoadingScheduledWorkouts)) return;

      // Load both in parallel but as a single "batch"
      await Promise.all([
        get().loadWorkouts(false, force),
        get().loadScheduledWorkouts(force),
      ]);
    },

    goToPreviousPeriod: () => {
      const { calendarViewMode } = get();
      set(state => {
        if (calendarViewMode === 'yearly') {
          return { currentMonth: subYears(state.currentMonth, 1) };
        } else if (calendarViewMode === 'quarterly') {
          return { currentMonth: subMonths(state.currentMonth, 3) };
        }
        return { currentMonth: subMonths(state.currentMonth, 1) };
      });
      get()._loadCalendarData();
    },

    goToNextPeriod: () => {
      const { calendarViewMode } = get();
      set(state => {
        if (calendarViewMode === 'yearly') {
          return { currentMonth: addYears(state.currentMonth, 1) };
        } else if (calendarViewMode === 'quarterly') {
          return { currentMonth: addMonths(state.currentMonth, 3) };
        }
        return { currentMonth: addMonths(state.currentMonth, 1) };
      });
      get()._loadCalendarData();
    },

    goToToday: () => {
      set({ currentMonth: new Date() });
      get()._loadCalendarData();
    },

    // Week navigation
    goToPreviousWeek: () => {
      set(state => ({
        currentWeekStart: subWeeks(state.currentWeekStart, 1)
      }));
      get()._loadCalendarData();
    },

    goToNextWeek: () => {
      set(state => ({
        currentWeekStart: addWeeks(state.currentWeekStart, 1)
      }));
      get()._loadCalendarData();
    },

    // Day navigation
    goToPreviousDay: () => {
      set(state => ({
        selectedDay: format(subDays(parseISO(state.selectedDay), 1), 'yyyy-MM-dd')
      }));
      get()._loadCalendarData();
    },

    goToNextDay: () => {
      set(state => ({
        selectedDay: format(addDays(parseISO(state.selectedDay), 1), 'yyyy-MM-dd')
      }));
      get()._loadCalendarData();
    },

    setSelectedDay: (day) => {
      const newDay = typeof day === 'string' ? day : format(day, 'yyyy-MM-dd');
      const { selectedDay: currentDay } = get();
      // Skip if day hasn't changed
      if (currentDay === newDay) return;
      set({ selectedDay: newDay });
      get()._loadCalendarData();
    },

    goToTodayWeek: () => {
      set({
        currentWeekStart: startOfWeek(new Date(), { weekStartsOn: 1 }),
        selectedDay: format(new Date(), 'yyyy-MM-dd'),
      });
      get()._loadCalendarData();
    },

    // Keep legacy function names for backwards compatibility
    goToPreviousMonth: () => get().goToPreviousPeriod(),
    goToNextMonth: () => get().goToNextPeriod(),

    setViewMode: (mode) => {
      set({ viewMode: mode });
      if (mode === 'analytics') {
        get().loadAnalytics();
      }
    },

    setCalendarViewMode: (mode) => {
      const { calendarViewMode: currentMode } = get();
      // Skip if mode hasn't changed
      if (currentMode === mode) return;

      set({ calendarViewMode: mode });
      // Use batched load to prevent duplicate requests
      get()._loadCalendarData();
    },

    toggleShowAllClientsCalendar: () => {
      const { showAllClientsCalendar } = get();
      set({ showAllClientsCalendar: !showAllClientsCalendar });
      // Reload scheduled workouts with new setting
      get().loadScheduledWorkouts(true);
    },

    // Get exercise history data (last + max) for auto-fill
    // Returns { last: {weight, reps}, max: {weight, reps} }
    // userId param allows coach to fetch client's exercise history
    // Searches both scheduled_workouts (new format) and fitness_workouts (old format)
    getExerciseHistory: async (exerciseName, userId = null) => {
      console.log('[getExerciseHistory] Looking for:', exerciseName, 'userId:', userId);
      try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.log('[getExerciseHistory] No user');
          return null;
        }

        const targetUserId = userId || user.id;
        const isCoachMode = userId && userId !== user.id;

        // ============================================
        // 0. Resolve aliases - get all related names
        // ============================================
        let allNames = [exerciseName];
        try {
          const { data: aliasData } = await supabase.rpc('resolve_exercise_aliases', {
            p_exercise_name: exerciseName
          });
          if (aliasData && aliasData.length > 0 && aliasData[0].all_names) {
            allNames = aliasData[0].all_names;
            console.log('[getExerciseHistory] Resolved aliases:', allNames);
          }
        } catch (aliasError) {
          console.log('[getExerciseHistory] Alias resolution failed, using original name:', aliasError);
        }

        let lastData = null; // Most recent
        let maxData = { weight: 0, reps: 0 }; // Best/highest

        // ============================================
        // 1. Query fitness_workouts table (old format)
        // ============================================
        console.log('[getExerciseHistory] Querying fitness_workouts table for names:', allNames);
        let fitnessWorkoutsData, fitnessWorkoutsError;

        if (isCoachMode) {
          // Coach fetching client's history - use RPC to bypass RLS
          // Query for each alias name
          const allResults = [];
          for (const name of allNames) {
            const result = await supabase.rpc('get_client_fitness_workouts', {
              p_client_id: userId,
              p_exercise_name: name
            });
            if (result.data) allResults.push(...result.data);
            if (result.error) fitnessWorkoutsError = result.error;
          }
          fitnessWorkoutsData = allResults;
        } else {
          // Fetching own history - direct query for all alias names
          const result = await supabase
            .from('fitness_workouts')
            .select('exercise_name, weight, reps, sets, created_at')
            .eq('user_id', targetUserId)
            .or(allNames.map(n => `exercise_name.ilike.${n}`).join(','))
            .order('created_at', { ascending: false })
            .limit(50);
          fitnessWorkoutsData = result.data;
          fitnessWorkoutsError = result.error;
        }

        console.log('[getExerciseHistory] fitness_workouts result:', { count: fitnessWorkoutsData?.length, error: fitnessWorkoutsError });

        if (!fitnessWorkoutsError && fitnessWorkoutsData?.length > 0) {
          for (const workout of fitnessWorkoutsData) {
            const weight = parseFloat(workout.weight) || 0;
            const reps = parseInt(workout.reps, 10) || 0;

            // Track last (first match since sorted desc)
            if (!lastData && (weight > 0 || reps > 0)) {
              lastData = { weight, reps };
            }

            // Track max weight (with corresponding reps)
            if (weight > maxData.weight) {
              maxData = { weight, reps };
            }
          }
        }

        // ============================================
        // 2. Query scheduled_workouts table (new format with planned_exercises JSONB)
        // ============================================
        console.log('[getExerciseHistory] Querying scheduled_workouts table');
        let scheduledData, scheduledError;

        if (isCoachMode) {
          // Coach fetching client's history - use RPC to bypass RLS
          const result = await supabase.rpc('get_client_exercise_history', {
            p_client_id: userId,
            p_exercise_name: exerciseName
          });
          scheduledData = result.data;
          scheduledError = result.error;
        } else {
          // Fetching own history - direct query
          const result = await supabase
            .from('scheduled_workouts')
            .select('planned_exercises, workout_completed_at')
            .eq('user_id', targetUserId)
            .eq('status', 'completed')
            .not('planned_exercises', 'is', null)
            .order('workout_completed_at', { ascending: false, nullsFirst: false })
            .limit(100);
          scheduledData = result.data;
          scheduledError = result.error;
        }

        console.log('[getExerciseHistory] scheduled_workouts result:', { count: scheduledData?.length, error: scheduledError });

        if (!scheduledError && scheduledData?.length > 0) {
          for (const workout of scheduledData) {
            const exercises = workout.planned_exercises || [];
            if (exercises.length === 0) continue;

            // Find exercise matching any of the alias names
            const exercise = exercises.find(ex =>
              allNames.some(name => ex.exercise_name?.toLowerCase() === name.toLowerCase())
            );

            if (exercise?.sets?.length > 0) {
              const setsWithData = exercise.sets.filter(s => s.actual_weight || s.actual_reps);

              for (const set of setsWithData) {
                const weight = parseFloat(set.actual_weight) || 0;
                const reps = parseInt(set.actual_reps, 10) || 0;

                // Track last (first match since sorted desc) - only if no data from fitness_workouts yet
                if (!lastData && (weight > 0 || reps > 0)) {
                  lastData = { weight, reps };
                }

                // Track max weight (with corresponding reps)
                if (weight > maxData.weight) {
                  maxData = { weight, reps };
                }
              }
            }
          }
        }

        // ============================================
        // 3. Return combined results
        // ============================================
        if (!lastData && maxData.weight === 0) {
          console.log('[getExerciseHistory] No data found for', exerciseName);
          return null;
        }

        const result = {
          last: lastData || { weight: 0, reps: 0 },
          max: maxData.weight > 0 ? maxData : (lastData || { weight: 0, reps: 0 })
        };
        console.log('[getExerciseHistory] Found:', result);
        return result;
      } catch (error) {
        console.error('[getExerciseHistory] Error:', error);
        return null;
      }
    },

    // =====================================================
    // MODALS
    // =====================================================

    openAddWorkoutModal: (date = null) => {
      set({
        isAddWorkoutModalOpen: true,
        selectedDate: date || format(new Date(), 'yyyy-MM-dd'),
        selectedWorkout: null,
      });
      // Lazy load exercise names when modal opens (if not already loaded)
      const { exerciseNames } = get();
      if (!exerciseNames || exerciseNames.length === 0) {
        get().loadExerciseNames();
      }
    },

    closeAddWorkoutModal: () => {
      set({
        isAddWorkoutModalOpen: false,
        selectedWorkout: null,
        selectedDate: null,
        completingScheduledWorkout: null, // Clear scheduled workout link
      });
    },

    // Start completing a scheduled workout - opens AddWorkoutModal with pre-filled data
    startCompletingScheduledWorkout: (scheduledWorkout) => {
      set({
        isScheduleWorkoutModalOpen: false, // Close schedule modal
        isAddWorkoutModalOpen: true, // Open add workout modal
        completingScheduledWorkout: scheduledWorkout, // Store the scheduled workout for linking
        selectedDate: scheduledWorkout.scheduled_date,
        selectedWorkout: null,
      });
      // Lazy load exercise names when modal opens
      const { exerciseNames } = get();
      if (!exerciseNames || exerciseNames.length === 0) {
        get().loadExerciseNames();
      }
    },

    // Open linked workout for viewing/editing (for completed scheduled workouts)
    openLinkedWorkout: async (scheduledWorkout) => {
      try {
        const { viewingClientId, isActingAsCoach } = useCoachStore.getState();
        const scheduledDate = scheduledWorkout.scheduled_date;

        let dateWorkouts = [];
        let targetClientId = null;

        // Determine if this is a client's workout that coach should fetch
        // scheduledWorkout.client_name exists only when coach views merged calendar (client's plan)
        // user_id in that case is the client's ID
        if (isActingAsCoach && scheduledWorkout.client_name && scheduledWorkout.user_id) {
          targetClientId = scheduledWorkout.user_id;
        } else if (viewingClientId) {
          targetClientId = viewingClientId;
        }

        console.log('[openLinkedWorkout]', {
          isActingAsCoach,
          hasClientName: !!scheduledWorkout.client_name,
          userId: scheduledWorkout.user_id,
          viewingClientId,
          targetClientId,
        });

        if (targetClientId) {
          // Coach mode: Fetch client's workouts for the date
          try {
            dateWorkouts = await coachRelationships.getClientWorkouts(
              targetClientId,
              scheduledDate,
              scheduledDate
            );
          } catch (err) {
            console.error('Error fetching client workouts:', err);
          }
        } else {
          // Normal user viewing own workouts
          dateWorkouts = get().getWorkoutsForDate(scheduledDate);
        }

        if (dateWorkouts && dateWorkouts.length > 0) {
          // Found workouts for this date - open day detail modal to show all
          set({
            isScheduleWorkoutModalOpen: false,
            isDayDetailModalOpen: true,
            selectedDate: scheduledDate,
            selectedDayWorkouts: dateWorkouts,
            selectedDayClientId: targetClientId || null, // Track client context for editing
          });
        } else {
          // No workouts found - open in create mode with pre-filled data
          set({
            isScheduleWorkoutModalOpen: false,
            isAddWorkoutModalOpen: true,
            completingScheduledWorkout: scheduledWorkout,
            selectedDate: scheduledDate,
            selectedWorkout: null,
          });
        }
      } catch (error) {
        console.error('Error opening linked workout:', error);
        toast.error('Antrenman detayları yüklenemedi');
      }
    },

    openGoalModal: () => set({ isGoalModalOpen: true }),
    closeGoalModal: () => set({ isGoalModalOpen: false }),

    openConnectSheetsModal: async () => {
      set({ isConnectSheetsModalOpen: true });
      // Lazy load: Check Google connection when modal opens
      await get().checkGoogleConnection();
      if (get().isGoogleConnected) {
        get().loadAvailableSpreadsheets();
        get().loadConnectedSpreadsheets();
      }
    },
    closeConnectSheetsModal: () => set({ isConnectSheetsModalOpen: false }),

    closeSchemaMapper: () => set({ isSchemaMapperOpen: false, schemaMapping: null }),

    openDayDetailModal: (date, workouts, clientId = null) => {
      set({
        isDayDetailModalOpen: true,
        selectedDate: date,
        selectedDayWorkouts: workouts,
        selectedDayClientId: clientId,
      });
    },
    closeDayDetailModal: () => set({
      isDayDetailModalOpen: false,
      selectedDayWorkouts: [],
      selectedDayClientId: null,
    }),

    // Exercise search modal
    openExerciseSearchModal: () => set({ isExerciseSearchModalOpen: true }),
    closeExerciseSearchModal: () => set({
      isExerciseSearchModalOpen: false,
      exerciseSearchResults: [],
      exerciseSearchQuery: '',
    }),
    searchExercises: async (searchTerm, clientId = null) => {
      set({ exerciseSearchQuery: searchTerm, isSearchingExercises: true });

      if (!searchTerm || searchTerm.trim().length < 2) {
        set({ exerciseSearchResults: [], isSearchingExercises: false });
        return;
      }

      try {
        // Use clientId for coach mode, otherwise search own workouts
        const { viewingClientId } = useCoachStore.getState();
        const targetUserId = clientId || viewingClientId || null;

        console.log('[fitnessStore.searchExercises] searchTerm:', searchTerm, 'clientId param:', clientId, 'viewingClientId:', viewingClientId, 'targetUserId:', targetUserId);

        let results;
        if (targetUserId) {
          // Coach mode: Use coachRelationships to get client workouts (RPC bypasses RLS)
          const allWorkouts = await coachRelationships.getClientWorkouts(targetUserId, null, null);
          // Filter by exercise name (case-insensitive)
          const searchLower = searchTerm.trim().toLowerCase();
          results = allWorkouts
            .filter(w => w.exercise_name?.toLowerCase().includes(searchLower))
            .sort((a, b) => b.date.localeCompare(a.date)); // Sort by date descending
        } else {
          // Normal user: search own workouts directly
          results = await fitness.searchExerciseHistory(searchTerm, null);
        }

        set({ exerciseSearchResults: results, isSearchingExercises: false });
      } catch (error) {
        console.error('Error searching exercises:', error);
        set({ exerciseSearchResults: [], isSearchingExercises: false });
        toast.error('Arama yapılırken hata oluştu');
      }
    },

    // Schedule workout modal
    openScheduleWorkoutModal: (date = null, time = null) => {
      set({
        isScheduleWorkoutModalOpen: true,
        scheduleModalDate: date || format(new Date(), 'yyyy-MM-dd'),
        scheduleModalTime: time || '10:00',
        selectedScheduledWorkout: null,
      });
    },

    closeScheduleWorkoutModal: () => {
      set({
        isScheduleWorkoutModalOpen: false,
        selectedScheduledWorkout: null,
        scheduleModalDate: null,
        scheduleModalTime: null,
      });
    },

    selectScheduledWorkoutForEdit: (workout) => {
      set({
        selectedScheduledWorkout: workout,
        scheduleModalDate: workout.scheduled_date,
        scheduleModalTime: workout.scheduled_time?.substring(0, 5),
        isScheduleWorkoutModalOpen: true,
      });
    },

    selectWorkoutForEdit: (workout) => {
      set({
        selectedWorkout: workout,
        selectedDate: workout.date,
        isAddWorkoutModalOpen: true,
      });
      // Lazy load exercise names when modal opens
      const { exerciseNames } = get();
      if (!exerciseNames || exerciseNames.length === 0) {
        get().loadExerciseNames();
      }
    },

    // =====================================================
    // LIVE WORKOUT (REAL-TIME SESSION)
    // =====================================================

    openLiveWorkoutModal: (scheduledWorkout) => {
      set({
        isLiveWorkoutModalOpen: true,
        liveWorkoutData: scheduledWorkout,
      });
    },

    closeLiveWorkoutModal: () => {
      set({
        isLiveWorkoutModalOpen: false,
        liveWorkoutData: null,
      });
    },

    startLiveWorkout: async (scheduledWorkoutId, coachParticipating = false) => {
      console.log('[startLiveWorkout] Starting workout:', scheduledWorkoutId);
      try {
        const { data, error } = await supabase.rpc('start_live_workout', {
          p_scheduled_workout_id: scheduledWorkoutId,
          p_coach_participating: coachParticipating,
        });

        console.log('[startLiveWorkout] RPC result:', { data, error });

        if (error) throw error;

        // Update local state
        set({ liveWorkoutData: data });

        // Update scheduledWorkouts list
        set(state => ({
          scheduledWorkouts: state.scheduledWorkouts.map(sw =>
            sw.id === scheduledWorkoutId ? data : sw
          ),
        }));

        return data;
      } catch (error) {
        console.error('Error starting live workout:', error);
        toast.error('Antrenman başlatılamadı');
        throw error;
      }
    },

    updateLiveWorkoutExercises: async (scheduledWorkoutId, exercises) => {
      try {
        set({ isLiveWorkoutSaving: true });

        const { data, error } = await supabase.rpc('update_planned_exercises', {
          p_scheduled_workout_id: scheduledWorkoutId,
          p_planned_exercises: exercises,
        });

        if (error) throw error;

        // Update local state
        set(state => ({
          liveWorkoutData: { ...state.liveWorkoutData, planned_exercises: exercises },
          isLiveWorkoutSaving: false,
        }));

        return data;
      } catch (error) {
        console.error('Error updating exercises:', error);
        set({ isLiveWorkoutSaving: false });
        throw error;
      }
    },

    completeLiveWorkout: async (scheduledWorkoutId) => {
      try {
        set({ isLiveWorkoutSaving: true });

        const { error } = await supabase.rpc('complete_live_workout', {
          p_scheduled_workout_id: scheduledWorkoutId,
        });

        if (error) throw error;

        set({ isLiveWorkoutSaving: false });

        // Reload both workouts and scheduled workouts to reflect changes
        await Promise.all([
          get().loadWorkouts(true),
          get().loadScheduledWorkouts(true),
        ]);

        return true;
      } catch (error) {
        console.error('Error completing workout:', error);
        set({ isLiveWorkoutSaving: false });
        toast.error('Antrenman tamamlanamadı');
        throw error;
      }
    },

    // =====================================================
    // HELPERS
    // =====================================================

    getWorkoutsForDate: (date) => {
      const { workouts } = get();
      const dateStr = typeof date === 'string' ? date : format(date, 'yyyy-MM-dd');
      return workouts.filter(w => w.date === dateStr);
    },

    getScheduledWorkoutsForDate: (date) => {
      const { scheduledWorkouts } = get();
      const dateStr = typeof date === 'string' ? date : format(date, 'yyyy-MM-dd');
      return scheduledWorkouts.filter(w => w.scheduled_date === dateStr);
    },

    getScheduledWorkoutsForTime: (date, hour) => {
      const { scheduledWorkouts } = get();
      const dateStr = typeof date === 'string' ? date : format(date, 'yyyy-MM-dd');
      return scheduledWorkouts.filter(w => {
        if (w.scheduled_date !== dateStr) return false;
        const workoutHour = parseInt(w.scheduled_time?.split(':')[0] || '0');
        return workoutHour === hour;
      });
    },

    formatDate: (date) => {
      const d = typeof date === 'string' ? parseISO(date) : date;
      if (isToday(d)) {
        return 'Bugün';
      }
      return format(d, 'd MMMM yyyy', { locale: tr });
    },

    // =====================================================
    // INITIALIZATION
    // =====================================================

    initialize: async (force = false) => {
      const { _isInitialized, _isInitializing, _lastInitClientId, _lastInitTime } = get();
      const { viewingClientId } = useCoachStore.getState();

      // Normalize to consistent value for comparison
      const currentClientId = viewingClientId || null;
      const lastClientId = _lastInitClientId;

      // Debounce: Skip if initialized within last 500ms (prevents race conditions)
      const now = Date.now();
      if (_isInitialized && now - _lastInitTime < 500) {
        return;
      }

      // Check if we need to reinitialize (client changed)
      const clientChanged = _isInitialized && lastClientId !== currentClientId;

      // Skip if already initialized for same client (unless forced or client changed)
      if (!force && !clientChanged && _isInitialized) {
        return;
      }

      // Skip if already initializing
      if (_isInitializing) {
        return;
      }

      set({ _isInitializing: true, _lastInitClientId: currentClientId, _lastInitTime: now });

      try {
        // Only load essential data for calendar view
        // Google Sheets, exercise names loaded on-demand when needed
        await Promise.all([
          get().loadWorkouts(false, true), // force reload
          get().loadGoals(true),
          get().loadScheduledWorkouts(true),
        ]);

        set({ _isInitialized: true, _isInitializing: false });

        // Subscribe to realtime updates for scheduled workouts
        get().subscribeToScheduledWorkouts();

        // Load these in background (non-blocking) - less critical for initial render
        get().loadPRs(true);
        get().loadSummaryStats(true);
        get().loadFavoriteExercises();
      } catch (error) {
        console.error('Error initializing fitness store:', error);
        set({ _isInitializing: false });
      }
    },
  }))
);

// Helper function to map workout data to spreadsheet row
function mapWorkoutToSheetRow(workout, mapping) {
  if (!mapping) return [];

  const row = [];
  const columnToIndex = (col) => col.charCodeAt(0) - 65;

  if (mapping.date_column) row[columnToIndex(mapping.date_column)] = workout.date;
  if (mapping.exercise_column) row[columnToIndex(mapping.exercise_column)] = workout.exercise_name;
  if (mapping.sets_column) row[columnToIndex(mapping.sets_column)] = workout.sets;
  if (mapping.reps_column) row[columnToIndex(mapping.reps_column)] = workout.reps;
  if (mapping.weight_column) row[columnToIndex(mapping.weight_column)] = workout.weight;
  if (mapping.notes_column) row[columnToIndex(mapping.notes_column)] = workout.notes;

  return row;
}

export default useFitnessStore;
